
import React from 'react';
import { CircleDecoration, WaveDivider, Confetti, FadeIn } from '../Animations';
import { Sparkles } from 'lucide-react';
import OptimizedFadeIn from '../OptimizedFadeIn';
import WaitlistForm from './WaitlistForm';
import CTAButton from './CTAButton';
import { useWaitlist } from '../../context/WaitlistContext';
import { useIsMobile } from '../../hooks/use-mobile';
import { AspectRatio } from '../ui/aspect-ratio';
import ImageOptimizer from '../ImageOptimizer';

interface HeroSectionProps {
  isAuthenticated: boolean;
}

const HeroSection: React.FC<HeroSectionProps> = ({
  isAuthenticated
}) => {
  const {
    email,
    setEmail,
    handleWaitlistSubmit,
    showConfetti,
    scrollToWaitlist
  } = useWaitlist();
  
  const isMobile = useIsMobile();
  
  return (
    <section className="relative min-h-[95vh] flex items-center overflow-hidden">
      {showConfetti && <Confetti colors={["#E27D60", "#F2A299", "#FFDFD3", "#FFB9A7"]} pieces={80} duration={3000} />}
      
      <CircleDecoration size={400} color="rgba(226, 125, 96, 0.05)" className="absolute -top-40 -left-40 z-0" />
      <CircleDecoration size={300} color="rgba(226, 125, 96, 0.07)" className="absolute bottom-20 -right-20 z-0" />
      
      <div className="container mx-auto px-4 z-10 relative py-[80px]">
        <div className={`flex flex-col ${isMobile ? 'items-center' : 'md:flex-row md:items-start md:gap-8'}`}>
          {/* Text content - now centered on mobile, left-aligned on desktop */}
          <div className={`${isMobile ? 'w-full text-center' : 'md:w-1/2 md:text-left'} max-w-3xl z-10 mb-8 md:mb-0`}>
            <OptimizedFadeIn>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-semibold leading-tight text-hormony-charcoal mb-6">
                30 Days to Hormonal Balance—Simple, Smart, & Science-Backed
              </h1>
            </OptimizedFadeIn>
            
            <OptimizedFadeIn>
              <p className="text-lg text-hormony-charcoal/80 mb-8 leading-relaxed">
                Daily 3-minute lessons, tiny habits, and expert insights—designed to help you feel like yourself again, with ease.
              </p>
            </OptimizedFadeIn>
            
            <OptimizedFadeIn>
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-hormony-beige/30 shadow-sm">
                <h3 className="text-xl font-semibold text-hormony-charcoal mb-2">By Invitation Only</h3>
                
                {isAuthenticated ? (
                  <div className="flex flex-col sm:flex-row gap-4 mt-4 justify-center md:justify-start">
                    <CTAButton to="/integrated-dashboard" variant="coral" className="w-full sm:w-auto">
                      Continue Your Journey
                    </CTAButton>
                  </div>
                ) : (
                  <div className="space-y-4 mt-2">
                    <div>
                      <p className="text-sm text-hormony-charcoal/70 mb-2">If you received an invite:</p>
                      <CTAButton to="/login" variant="coral" className="w-full sm:w-auto">
                        Log In
                      </CTAButton>
                    </div>
                    
                    <div>
                      <p className="text-sm text-hormony-charcoal/70 mb-2">Want to join?</p>
                      <WaitlistForm email={email} setEmail={setEmail} handleWaitlistSubmit={handleWaitlistSubmit} />
                    </div>
                  </div>
                )}
              </div>
            </OptimizedFadeIn>
            
            {/* Science-backed badge */}
            <OptimizedFadeIn>
              <div className="flex items-center justify-center md:justify-start gap-2 mt-8 bg-[#E27D60]/10 p-3 px-5 rounded-full max-w-md mx-auto md:mx-0 mb-10">
                <Sparkles className="text-[#E27D60]" size={20} />
                <span className="text-sm font-medium text-hormony-charcoal">Backed by science, designed for lasting change</span>
              </div>
            </OptimizedFadeIn>
          </div>

          <div className="md:w-1/2 relative">
            {/* Direct image element with fixed size and proper loading attributes */}
            <div className="relative rounded-2xl overflow-hidden shadow-lg">
              <img 
                src="/lovable-uploads/ac7da958-8971-47d6-8588-0611fa790b37.png" 
                alt="Woman with peaceful expression" 
                className="w-full h-auto rounded-2xl"
                width={600}
                height={800}
                loading="eager"
                onError={(e) => {
                  console.error("Image failed to load:", e);
                  // Fallback to another image if this one fails
                  e.currentTarget.src = "/placeholder.svg";
                }}
                style={{
                  objectFit: 'cover',
                  display: 'block'
                }}
              />
            </div>
          </div>
        </div>
      </div>
      
      <div className="absolute bottom-0 left-0 w-full">
        <WaveDivider color="#F9F5F0" />
      </div>
    </section>
  );
};

export default React.memo(HeroSection);
